'use client';

import * as React from 'react';
import * as D from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { cn } from './cn';
import { IconButton } from './IconButton';
import { useIsMobile } from './hooks';
import { useUILabels } from './UIProvider';

/** `auto` = bottom sheet on phones, side panel from the reading end on desktop. */
export type SheetSide = 'auto' | 'bottom' | 'start' | 'end';
export type SheetSize = 'sm' | 'md' | 'lg' | 'full';

export const SheetRoot = D.Root;
export const SheetTrigger = D.Trigger;
export const SheetClose = D.Close;

const SIDE_PANEL_WIDTH: Record<SheetSize, string> = {
  sm: 'w-[min(88vw,20rem)]',
  md: 'w-[min(92vw,24rem)]',
  lg: 'w-[min(94vw,32rem)]',
  full: 'w-screen',
};

const BOTTOM_HEIGHT: Record<SheetSize, string> = {
  sm: 'max-h-[45dvh]',
  md: 'max-h-[70dvh]',
  lg: 'max-h-[88dvh]',
  full: 'h-[100dvh]',
};

export interface SheetProps extends Omit<React.ComponentPropsWithoutRef<typeof D.Content>, 'title'> {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  trigger?: React.ReactNode;
  side?: SheetSide;
  size?: SheetSize;
  title: React.ReactNode;
  description?: React.ReactNode;
  hideTitle?: boolean;
  footer?: React.ReactNode;
  hideClose?: boolean;
  /** Accessible name of the ✕. Defaults to the localised `close` label from `<UIProvider>` (UX-42). */
  closeLabel?: string;
  /** Drag handle on bottom sheets (default true). Drag down past ~96 px to dismiss. */
  dragHandle?: boolean;
  /** `false` lets the user keep interacting with the canvas behind the sheet. */
  modal?: boolean;
  /** Extra classes for the scrollable body. */
  bodyClassName?: string;
}

/**
 * The editor's main disclosure surface.
 *
 * Phones get a bottom sheet with a drag handle so the spread above stays visible;
 * desktop gets a side panel anchored to the reading end. Same component, same
 * children — `side="auto"` picks per viewport.
 */
export function Sheet({
  open,
  defaultOpen,
  onOpenChange,
  trigger,
  side = 'auto',
  size = 'md',
  title,
  description,
  hideTitle,
  footer,
  hideClose,
  closeLabel,
  dragHandle = true,
  modal = true,
  className,
  bodyClassName,
  children,
  ...props
}: SheetProps) {
  const labels = useUILabels();
  const isMobile = useIsMobile();
  const resolved: Exclude<SheetSide, 'auto'> = side === 'auto' ? (isMobile ? 'bottom' : 'end') : side;
  const isBottom = resolved === 'bottom';

  const contentRef = React.useRef<HTMLDivElement>(null);
  const [dragY, setDragY] = React.useState<number | null>(null);
  const startY = React.useRef(0);

  const onDragStart = (e: React.PointerEvent) => {
    if (!isBottom || !dragHandle) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    startY.current = e.clientY;
    setDragY(0);
  };
  const onDragMove = (e: React.PointerEvent) => {
    if (dragY === null) return;
    setDragY(Math.max(0, e.clientY - startY.current));
  };
  const onDragEnd = (e: React.PointerEvent) => {
    if (dragY === null) return;
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
    if (dragY > 96) onOpenChange?.(false);
    setDragY(null);
  };

  const animation = isBottom
    ? 'data-[state=open]:animate-sheet-up data-[state=closed]:animate-sheet-down'
    : resolved === 'end'
      ? 'data-[state=open]:animate-sheet-in-end data-[state=closed]:animate-sheet-out-end'
      : 'data-[state=open]:animate-sheet-in-start data-[state=closed]:animate-sheet-out-start';

  const position = isBottom
    ? cn('inset-x-0 bottom-0 rounded-t-2xl', BOTTOM_HEIGHT[size])
    : cn(
        'inset-y-0 h-[100dvh]',
        resolved === 'end' ? 'end-0 rounded-s-xl' : 'start-0 rounded-e-xl',
        SIDE_PANEL_WIDTH[size],
      );

  return (
    <D.Root open={open} defaultOpen={defaultOpen} onOpenChange={onOpenChange} modal={modal}>
      {trigger ? <D.Trigger asChild>{trigger}</D.Trigger> : null}
      <D.Portal>
        {modal ? (
          <D.Overlay
            className={cn(
              'fixed inset-0 z-(--z-sheet-modal) bg-overlay',
              'data-[state=open]:animate-fade-in data-[state=closed]:animate-fade-out',
            )}
          />
        ) : null}
        <D.Content
          ref={contentRef}
          onOpenAutoFocus={(e) => {
            // Don't yank the keyboard open on phones; focus the panel itself.
            if (isMobile) {
              e.preventDefault();
              contentRef.current?.focus();
            }
          }}
          style={dragY !== null ? { transform: `translateY(${dragY}px)`, transition: 'none' } : undefined}
          className={cn(
            'fixed flex flex-col overflow-hidden border-line bg-surface shadow-sheet',
            // Modal sheets clear the editor's mobile tab bar; the tool panel is deliberately not
            // modal, so it stays below the bar and the two can be used together.
            modal ? 'z-(--z-sheet-modal)' : 'z-(--z-sheet)',
            'focus-visible:outline-none',
            isBottom ? 'border-t' : resolved === 'end' ? 'border-s' : 'border-e',
            'pb-[env(safe-area-inset-bottom)]',
            animation,
            position,
            className,
          )}
          {...props}
        >
          {isBottom && dragHandle ? (
            <div
              onPointerDown={onDragStart}
              onPointerMove={onDragMove}
              onPointerUp={onDragEnd}
              onPointerCancel={onDragEnd}
              className="flex cursor-grab touch-none justify-center py-2.5 active:cursor-grabbing"
              aria-hidden
            >
              <span className="h-1 w-10 rounded-full bg-line-strong" />
            </div>
          ) : null}

          <div
            className={cn(
              'flex items-start gap-3 px-4 pb-3',
              isBottom && dragHandle ? 'pt-0' : 'pt-4',
              'border-b border-line',
            )}
          >
            <div className="min-w-0 flex-1">
              {hideTitle ? (
                <D.Title className="sr-only">{title}</D.Title>
              ) : (
                <D.Title className="truncate text-base font-semibold text-ink">{title}</D.Title>
              )}
              {description ? (
                <D.Description className="mt-0.5 text-xs text-ink-2">{description}</D.Description>
              ) : null}
            </div>
            {!hideClose ? (
              <D.Close asChild>
                <IconButton label={closeLabel ?? labels.close} size="sm" variant="ghost" className="-me-1.5 -mt-1">
                  <X />
                </IconButton>
              </D.Close>
            ) : null}
          </div>

          <div className={cn('min-h-0 flex-1 overflow-y-auto overscroll-contain p-4', bodyClassName)}>
            {children}
          </div>

          {footer ? (
            <div className="flex items-center gap-2 border-t border-line bg-paper-2 px-4 py-3">{footer}</div>
          ) : null}
        </D.Content>
      </D.Portal>
    </D.Root>
  );
}
